import { isApiError } from '@/types/api'
import { listVoices, addVoice as apiAddVoice, deleteVoice as apiDeleteVoice } from '@/utils/api'
import type { VoiceConfig, AddVoiceRequest } from '@/types/voices'
import type { Commit, Dispatch } from 'vuex'

export interface VoicesState {
  configs: Record<string, VoiceConfig>       // keyed by name
  configsById: Record<string, VoiceConfig>   // keyed by id
  loading: boolean
  error: string | null
}

export const voices = () => ({
  state: <VoicesState>{
    configs: {},
    configsById: {},
    loading: false,
    error: null
  },
  mutations: {
    setVoiceConfigs(state: VoicesState, configs: Record<string, VoiceConfig>) {
      state.configs = configs
    },
    setVoiceConfigsById(state: VoicesState, configs: Record<string, VoiceConfig>) {
      state.configsById = configs
    },
    deleteVoice(state: VoicesState, name: string) {
      const cfg = state.configs[name]
      if (cfg) {
        delete state.configs[name]
        delete state.configsById[cfg.id]
      }
    },
    setVoicesLoading(state: VoicesState, loading: boolean) {
      state.loading = loading
    },
    setVoicesError(state: VoicesState, error: string | null) {
      state.error = error
    }
  },
  actions: {
    async fetchVoices({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }) {
      commit('setVoicesLoading', true)
      commit('setVoicesError', null)
      const resp = await listVoices()
      if (isApiError(resp)) {
        commit('setVoicesError', resp.message)
        dispatch('updateSnackbar', { text: `Voices Error: ${resp.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Voices Error', content: resp }, { root: true })
      } else {
        const list: VoiceConfig[] = resp.data || []
        const byName: Record<string, VoiceConfig> = {}
        const byId: Record<string, VoiceConfig> = {}
        for (const v of list) {
          byName[v.name] = v
          byId[v.id] = v
        }
        commit('setVoiceConfigs', byName)
        commit('setVoiceConfigsById', byId)
        dispatch('addLogEvent', { title: 'Voices Fetched', content: { count: list.length } }, { root: true })
      }
      commit('setVoicesLoading', false)
    },
    async setVoiceConfig(
      { dispatch }: { dispatch: Dispatch },
      data: AddVoiceRequest
    ) {
      const resp = await apiAddVoice(data)
      if (isApiError(resp)) {
        dispatch('updateSnackbar', { text: `Voice Add Error: ${resp.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Voice Add Error', content: resp }, { root: true })
        return false
      } else {
        dispatch('updateSnackbar', { text: resp.message || 'Voice saved' }, { root: true })
        dispatch('addLogEvent', { title: 'Voice Added', content: data }, { root: true })
        await dispatch('fetchVoices')
        return true
      }
    },
    async deleteVoice({ commit, dispatch }: { commit: Commit; dispatch: Dispatch }, name: string) {
      const resp = await apiDeleteVoice(name)
      if (isApiError(resp)) {
        dispatch('updateSnackbar', { text: `Voice Delete Error: ${resp.message}` }, { root: true })
        dispatch('addLogEvent', { title: 'Voice Delete Error', content: resp }, { root: true })
        return false
      } else {
        commit('deleteVoice', name)
        dispatch('updateSnackbar', { text: resp.message }, { root: true })
        dispatch('addLogEvent', { title: 'Voice Deleted', content: name }, { root: true })
        await dispatch('fetchVoices')
        return true
      }
    }
  },
  getters: {
    voiceConfigs: (state: VoicesState) => state.configs,
    voiceConfigsById: (state: VoicesState) => state.configsById,
    voiceConfigByName: (state: VoicesState) => (name: string) => state.configs[name],
    voiceConfigById: (state: VoicesState) => (id: string) => state.configsById[id],
    voiceConfigsList: (state: VoicesState) => Object.values(state.configsById)
  }
})
